import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../../shared/lib/supabase'
import { mapPipelineError } from './pipelineErrorMap'
import { extractTextFromStoragePath } from '../../shared/utils/pdfTextExtract'

export const STAGE_WEIGHTS = { analyst: 2, specialist: 49, qa: 49 }
export const POLL_INTERVAL_MS = 5000
export const IN_PROGRESS_WINDOW_MS = 10 * 60 * 1000

const TERMINAL_TYPES = new Set(['qa_call', 'pipeline_failed'])

// Resolve the active policy term for this client, or create one if none exists.
// The v2 pipeline endpoint requires a policy_term_id to anchor the review.
// Returns { id, wasCreated }.
async function resolveOrCreateActiveTerm(clientId) {
  const { data: terms, error: termsErr } = await supabase
    .from('client_insurance_policy_terms')
    .select('id, effective_date, status')
    .eq('client_id', clientId)
    .eq('status', 'active')
    .order('effective_date', { ascending: false })
    .limit(1)
  if (termsErr) throw new Error(`Failed to look up policy term: ${termsErr.message}`)
  if (terms && terms.length > 0) return { id: terms[0].id, wasCreated: false }

  const today = new Date().toISOString().slice(0, 10)
  const oneYearOut = new Date()
  oneYearOut.setFullYear(oneYearOut.getFullYear() + 1)
  const { data: created, error: createErr } = await supabase
    .from('client_insurance_policy_terms')
    .insert({
      client_id: clientId,
      status: 'active',
      layer_type: 'single',
      effective_date: today,
      expiration_date: oneYearOut.toISOString().slice(0, 10),
      notes: 'Auto-created on first upload'
    })
    .select('id')
    .single()
  if (createErr) throw new Error(`Failed to create policy term: ${createErr.message}`)
  return { id: created.id, wasCreated: true }
}

// Given the audit rows for a correlation_id (chronological ascending), reduce
// them into the stage-state map that the tracker UI renders from.
export function reconstructStagesFromAuditRows(rows) {
  const stages = { analyst: 'pending', specialist: 'pending', qa: 'pending' }
  let error = null
  for (const row of rows || []) {
    if (row.event_type === 'analyst_call') stages.analyst = 'done'
    else if (row.event_type === 'specialist_call') stages.specialist = 'done'
    else if (row.event_type === 'qa_call') stages.qa = 'done'
    else if (row.event_type === 'pipeline_failed') {
      const failedStage = row.error_details?.stage
      if (failedStage && stages[failedStage] !== undefined) stages[failedStage] = 'failed'
      error = row.error_details || null
    }
  }
  // Promote first pending → active only when the pipeline has actually started
  // (i.e., at least one event row exists) and nothing has failed.
  if (!error && rows && rows.length > 0) {
    if (stages.analyst === 'pending') stages.analyst = 'active'
    else if (stages.specialist === 'pending') stages.specialist = 'active'
    else if (stages.qa === 'pending') stages.qa = 'active'
  }
  stages.error = error
  return stages
}

export function isTerminalRun(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return false
  return rows.some(r => TERMINAL_TYPES.has(r.event_type))
}

// Weighted progress percentage. Elapsed ms drives sub-stage growth.
// Clamped at 95 until the caller marks the run as complete.
export function computeProgressPercent(stages, elapsedMsInActiveStage) {
  let pct = 0
  if (stages.analyst === 'done') pct += STAGE_WEIGHTS.analyst
  if (stages.specialist === 'done') pct += STAGE_WEIGHTS.specialist
  if (stages.qa === 'done') pct += STAGE_WEIGHTS.qa

  const activeStage = stages.analyst === 'active' ? 'analyst'
    : stages.specialist === 'active' ? 'specialist'
    : stages.qa === 'active' ? 'qa'
    : null

  if (activeStage) {
    const weight = STAGE_WEIGHTS[activeStage]
    // Assume typical stage duration ~ 2 minutes; smoothly fill toward the weight.
    const typicalMs = activeStage === 'analyst' ? 8000 : 120000
    const stageFraction = Math.min(0.98, elapsedMsInActiveStage / typicalMs)
    pct += Math.round(weight * stageFraction)
  }

  return Math.min(95, pct)
}

// Derive a stages map for the failure view. Respects prior polled state so
// we don't misrepresent which stage actually failed. When the server tells
// us the failing stage, mark it failed and everything earlier done. When
// stage is unknown (e.g. pre-pipeline 400/401), keep the current snapshot
// and just mark whichever stage is currently active as failed, or the whole
// row as failed if nothing has started yet.
export function buildFailedStages(currentStages, failedStage) {
  const s = currentStages || { analyst: 'pending', specialist: 'pending', qa: 'pending' }
  if (failedStage === 'analyst') return { analyst: 'failed', specialist: 'pending', qa: 'pending', error: null }
  if (failedStage === 'specialist') return { analyst: 'done', specialist: 'failed', qa: 'pending', error: null }
  if (failedStage === 'qa') return { analyst: 'done', specialist: 'done', qa: 'failed', error: null }
  const active = s.analyst === 'active' ? 'analyst'
    : s.specialist === 'active' ? 'specialist'
    : s.qa === 'active' ? 'qa'
    : null
  if (!active) {
    // Nothing started yet — mark the first pending stage as failed
    if (s.analyst === 'pending') return { analyst: 'failed', specialist: 'pending', qa: 'pending', error: null }
    if (s.specialist === 'pending') return { ...s, specialist: 'failed', error: null }
    return { ...s, qa: 'failed', error: null }
  }
  return { ...s, [active]: 'failed', error: null }
}

const emptyState = () => ({
  view: 'upload', // 'upload' | 'running' | 'failed' | 'done'
  correlationId: null,
  stages: { analyst: 'pending', specialist: 'pending', qa: 'pending', error: null },
  runStartedAt: null,
  activeStageStartedAt: null,
  error: null // { advisorText, internalCode, httpStatus, stage }
})

export default function useInsuranceReviewPipeline({ clientId, onReviewReady }) {
  const [state, setState] = useState(emptyState)
  const pollTimer = useRef(null)
  const stopPolling = () => {
    if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null }
  }

  const pollAudit = useCallback(async (correlationId, runStartedAt) => {
    const { data: rows } = await supabase
      .from('client_insurance_review_audit')
      .select('event_type, error_details, created_at')
      .eq('correlation_id', correlationId)
      .order('created_at', { ascending: true })
    const stages = reconstructStagesFromAuditRows(rows)
    setState(prev => ({
      ...prev,
      stages,
      activeStageStartedAt: prev.activeStageStartedAt || runStartedAt
    }))
    if (stages.error) {
      stopPolling()
      const httpStatus = stages.error.http_status
      const stage = stages.error.stage
      const err = mapPipelineError({ httpStatus, stage })
      setState(prev => ({ ...prev, view: 'failed', error: { ...err, httpStatus, stage } }))
    }
  }, [])

  // Detect an in-progress run on mount so refresh mid-flight resumes gracefully.
  useEffect(() => {
    if (!clientId) return
    let cancelled = false
    ;(async () => {
      const cutoff = new Date(Date.now() - IN_PROGRESS_WINDOW_MS).toISOString()
      const { data: rows } = await supabase
        .from('client_insurance_review_audit')
        .select('event_type, error_details, correlation_id, created_at')
        .eq('client_id', clientId)
        .gte('created_at', cutoff)
        .order('created_at', { ascending: true })
      if (cancelled || !rows || rows.length === 0) return
      // Group by correlation_id, pick newest non-terminal group
      const byRun = new Map()
      for (const r of rows) {
        if (!r.correlation_id) continue
        if (!byRun.has(r.correlation_id)) byRun.set(r.correlation_id, [])
        byRun.get(r.correlation_id).push(r)
      }
      let inFlight = null
      for (const [cid, runRows] of byRun) {
        if (!isTerminalRun(runRows)) inFlight = { correlationId: cid, rows: runRows }
      }
      if (!inFlight) return
      const startedAt = new Date(inFlight.rows[0].created_at).getTime()
      const stages = reconstructStagesFromAuditRows(inFlight.rows)
      setState({
        view: 'running',
        correlationId: inFlight.correlationId,
        stages,
        runStartedAt: startedAt,
        activeStageStartedAt: startedAt,
        error: null
      })
      pollTimer.current = setInterval(() => pollAudit(inFlight.correlationId, startedAt), POLL_INTERVAL_MS)
    })()
    return () => { cancelled = true; stopPolling() }
  }, [clientId, pollAudit])

  const submit = useCallback(async ({ mode, documents, reviewKind, parentReviewId, policyTermId: policyTermIdOverride }) => {
    stopPolling()
    const correlationId = crypto.randomUUID()
    const startedAt = Date.now()
    setState({
      view: 'running',
      correlationId,
      stages: { analyst: 'active', specialist: 'pending', qa: 'pending', error: null },
      runStartedAt: startedAt,
      activeStageStartedAt: startedAt,
      error: null
    })

    pollTimer.current = setInterval(() => pollAudit(correlationId, startedAt), POLL_INTERVAL_MS)

    try {
      const term = await resolveOrCreateActiveTerm(clientId)
      const session = (await supabase.auth.getSession()).data.session
      const res = await fetch('/api/ai/insurance-review-v2', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token || ''}`
        },
        body: JSON.stringify({
          mode, clientId, correlationId, documents,
          policyTermId: policyTermIdOverride || term.id,
          reviewKind: reviewKind || 'initial',
          parentReviewId: parentReviewId || null
        })
      })
      stopPolling()
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        // Reconstruct which stage actually failed from the server response.
        // Fall back to the response's stage field, then the current stages
        // snapshot (in case polling saw a stage transition just before we lost).
        const failedStage = body.stage
        const err = mapPipelineError({ httpStatus: res.status, stage: failedStage })
        setState(prev => ({
          view: 'failed',
          correlationId,
          stages: buildFailedStages(prev.stages, failedStage),
          runStartedAt: startedAt,
          activeStageStartedAt: startedAt,
          error: { ...err, httpStatus: res.status, stage: failedStage, message: body.error }
        }))
        return
      }
      const result = await res.json()
      setState(prev => ({
        ...prev,
        view: 'done',
        stages: { analyst: 'done', specialist: 'done', qa: 'done', error: null }
      }))
      onReviewReady?.(result)
    } catch (err) {
      stopPolling()
      const mapped = mapPipelineError({ httpStatus: 500 })
      setState(prev => ({
        ...prev,
        view: 'failed',
        error: { ...mapped, httpStatus: 500, message: err?.message }
      }))
    }
  }, [clientId, onReviewReady, pollAudit])

  const retry = useCallback(async () => {
    if (!state.correlationId) return
    const parentCorrelationId = state.correlationId
    const failedStage = state.error?.stage
    const resumeFromStage = failedStage === 'qa' ? 'qa' : 'specialist'
    stopPolling()
    const correlationId = crypto.randomUUID()
    const startedAt = Date.now()
    setState(prev => ({
      view: 'running',
      correlationId,
      stages: {
        analyst: 'done',
        specialist: resumeFromStage === 'specialist' ? 'active' : 'done',
        qa: resumeFromStage === 'qa' ? 'active' : 'pending',
        error: null
      },
      runStartedAt: startedAt,
      activeStageStartedAt: startedAt,
      error: null
    }))
    pollTimer.current = setInterval(() => pollAudit(correlationId, startedAt), POLL_INTERVAL_MS)
    try {
      const session = (await supabase.auth.getSession()).data.session
      const res = await fetch('/api/ai/insurance-review-v2', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token || ''}`
        },
        body: JSON.stringify({ clientId, correlationId, parentCorrelationId, resumeFromStage, policyTermId: (await resolveOrCreateActiveTerm(clientId)).id })
      })
      stopPolling()
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        const failedStage = body.stage
        const err = mapPipelineError({ httpStatus: res.status, stage: failedStage })
        setState(prev => ({
          ...prev,
          view: 'failed',
          stages: buildFailedStages(prev.stages, failedStage),
          error: { ...err, httpStatus: res.status, stage: failedStage, message: body.error }
        }))
        return
      }
      const result = await res.json()
      setState(prev => ({
        ...prev,
        view: 'done',
        stages: { analyst: 'done', specialist: 'done', qa: 'done', error: null }
      }))
      onReviewReady?.(result)
    } catch (err) {
      stopPolling()
      const mapped = mapPipelineError({ httpStatus: 500 })
      setState(prev => ({
        ...prev,
        view: 'failed',
        error: { ...mapped, httpStatus: 500, message: err?.message }
      }))
    }
  }, [state.correlationId, state.error, clientId, onReviewReady, pollAudit])

  const startOver = useCallback(() => {
    stopPolling()
    setState(emptyState())
  }, [])

  return { ...state, submit, retry, startOver }
}
